// PR-WATER-SUPPLIERS MAY 11 — list + edit filling stations.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Station {
  id: string;
  code: string;
  name_en: string;
  name_ar: string | null;
  supplier_id: string;
  supplier_code: string | null;
  supplier_name: string | null;
  serves_site_id: string | null;
  site_name: string | null;
  city: string | null;
  region: string | null;
  rate_per_m3_sar: string;
  vat_pct: string | null;
  status: string;
}

interface Supplier { id: string; code: string; name_en: string }
interface Site { id: string; nameEn?: string; name_en?: string }

export default function WaterStationsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'accountant';

  const [items, setItems] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Station | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiClient.get<{ items: Station[] }>('/api/v1/water-stations');
      setItems(r.data.items ?? []);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard/water-suppliers"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Suppliers</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><MapPin className="h-4 w-4" /> Filling stations</div>
          {canEdit ? (
            <Button variant="gold" size="sm" onClick={() => setCreating(true)} className="gap-1"><Plus className="h-4 w-4" /> Add station</Button>
          ) : <div className="w-24" />}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {error && <PageError error={error} />}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground border-b border-border"><tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Code</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Station</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Supplier</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Serves site</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium">Rate (SAR/m³)</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">Status</th>
              </tr></thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} onClick={() => canEdit && setEditing(s)} className={'border-b border-border last:border-b-0 ' + (canEdit ? 'cursor-pointer hover:bg-muted/40' : '')}>
                    <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                    <td className="px-4 py-3">
                      <div>{s.name_en}</div>
                      {s.name_ar && <div className="text-xs text-muted-foreground" dir="rtl">{s.name_ar}</div>}
                    </td>
                    <td className="px-4 py-3">{s.supplier_name ?? '—'}</td>
                    <td className="px-4 py-3">{s.site_name ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{Number(s.rate_per_m3_sar).toFixed(3)}</td>
                    <td className="px-4 py-3">
                      <span className={'text-[11px] px-2 py-0.5 rounded-full ' + (s.status === 'active' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30' : 'bg-muted/40 text-muted-foreground border border-border')}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground italic">No stations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <StationDrawer
        mode="edit"
        station={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={refresh}
      />
      <StationDrawer
        mode="create"
        station={null}
        open={creating}
        onOpenChange={setCreating}
        onSaved={refresh}
      />
    </div>
  );
}

function StationDrawer({ mode, station, open, onOpenChange, onSaved }: {
  mode: 'create' | 'edit';
  station: Station | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(''); const [nameEn, setNameEn] = useState('');
  const [nameAr, setNameAr] = useState(''); const [supplierId, setSupplierId] = useState('');
  const [siteId, setSiteId] = useState(''); const [rate, setRate] = useState('');
  const [city, setCity] = useState(''); const [region, setRegion] = useState('');
  const [vatPct, setVatPct] = useState('15');
  const [status, setStatus] = useState('active');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (station) {
      setCode(station.code); setNameEn(station.name_en); setNameAr(station.name_ar ?? '');
      setSupplierId(station.supplier_id); setSiteId(station.serves_site_id ?? '');
      setRate(String(station.rate_per_m3_sar)); setCity(station.city ?? '');
      setRegion(station.region ?? ''); setVatPct(String(station.vat_pct ?? '15'));
      setStatus(station.status);
    } else {
      setCode(''); setNameEn(''); setNameAr(''); setSupplierId('');
      setSiteId(''); setRate(''); setCity(''); setRegion(''); setVatPct('15'); setStatus('active');
    }
    setError(null);
    Promise.all([
      apiClient.get<{ items: Supplier[] }>('/api/v1/water-suppliers').then((r) => setSuppliers(r.data.items ?? [])),
      apiClient.get<{ items: Site[] } | Site[]>('/api/v1/sites?pageSize=200').then((r) => {
        const data = Array.isArray(r.data) ? r.data : (r.data.items ?? []);
        setSites(data);
      }),
    ]).catch(() => { /* swallow */ });
  }, [open, station]);

  async function save() {
    setSaving(true); setError(null);
    try {
      const payload: Record<string, unknown> = {
        code, nameEn, nameAr: nameAr || null,
        supplierId, servesSiteId: siteId || null,
        city: city || null, region: region || null,
        ratePerM3Sar: Number(rate), vatPct: Number(vatPct),
        status,
      };
      if (mode === 'create') {
        await apiClient.post('/api/v1/water-stations', payload);
      } else if (station) {
        await apiClient.patch('/api/v1/water-stations/' + station.id, payload);
      }
      onSaved();
      onOpenChange(false);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Add filling station' : `Edit · ${station?.code}`}</SheetTitle>
          <SheetDescription>Stations are linked to a water supplier and (optionally) a site they serve. Rate is the base SAR per m³ excluding VAT.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Code *</Label><Input value={code} onChange={(e) => setCode(e.target.value)} className="font-mono" disabled={mode === 'edit'} /></div>
            <div className="space-y-1"><Label>Status</Label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                <option value="active">Active</option><option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="space-y-1"><Label>Name (EN) *</Label><Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} /></div>
          <div className="space-y-1"><Label>Name (AR)</Label><Input value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" /></div>
          <div className="space-y-1"><Label>Supplier *</Label>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
              <option value="">— Select —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name_en}</option>)}
            </select>
          </div>
          <div className="space-y-1"><Label>Serves site</Label>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
              <option value="">— None —</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.nameEn ?? s.name_en ?? s.id}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>City</Label><Input value={city} onChange={(e) => setCity(e.target.value)} /></div>
            <div className="space-y-1"><Label>Region</Label><Input value={region} onChange={(e) => setRegion(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Rate (SAR/m³, excl VAT) *</Label><Input type="number" step="0.001" value={rate} onChange={(e) => setRate(e.target.value)} className="font-mono" /></div>
            <div className="space-y-1"><Label>VAT %</Label><Input type="number" step="0.01" value={vatPct} onChange={(e) => setVatPct(e.target.value)} className="font-mono" /></div>
          </div>
          {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>}
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !code || !nameEn || !supplierId || !rate} className="gap-1">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {mode === 'create' ? 'Create station' : 'Save changes'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
